/**
 * Transaction write helpers — web SDK port of
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/transactions/transactions.ts
 *
 * Same Firestore shape, same field names. Mobile + web write the
 * exact same documents so the two clients stay in lockstep.
 *
 * What's in this turn:
 *   - createTransaction (this file)
 *   - updateTransaction (next turn)
 *   - approveTransaction / rejectTransaction / clearTransactionSettlement
 *     (next turn)
 *   - deleteTransaction (next turn)
 */
import {
  addDoc,
  collection,
  deleteField,
  doc as fsDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// TransactionType + PaymentMethod live in `@shared/perms/transaction`
// now so mobile and web share identical enums. Re-exported here so
// existing imports keep working.
import type { TransactionType, PaymentMethod } from '@shared/perms';
export type { TransactionType, PaymentMethod };

/** Cost code / category for the transaction.
 *
 *  Two flavours discriminated by `TransactionType`:
 *    - INCOMING (payment_in)  — money FROM the client TO the studio
 *      (token, design advance, 3D render advance, site mobilisation,
 *      milestone payment, final payment, others).
 *    - OUTGOING (payment_out) — money the studio spends (labour,
 *      material, transport, sub-contractor, salary, rent, etc.).
 *
 *  Pickers MUST filter via `TRANSACTION_CATEGORIES_BY_TYPE` below
 *  so the IN dropdown never shows labour/material and the OUT
 *  dropdown never shows token_amount/design_advance.
 *
 *  MIRRORS `src/features/transactions/types.ts` (mobile) — keep
 *  the two in lock-step. */
/** Built-in transaction category enum. CUSTOM categories (added
 *  via the picker's "+ Add new" affordance) persist their LITERAL
 *  LABEL on the doc. So `TransactionCategory` is broadened below
 *  to `string` everywhere it appears on a doc. Display layer falls
 *  back to showing the raw string when it doesn't match a built-in
 *  key. */
export type TransactionBuiltInCategory =
  // ── INCOMING (client → studio) ──
  | 'token_amount'
  | 'design_advance'
  | 'render_advance'
  | 'site_mobilization'
  | 'milestone_payment'
  | 'final_payment'
  // ── OUTGOING (studio → vendors / labour / etc.) ──
  | 'labour'
  | 'material'
  | 'transport'
  | 'equipment'
  | 'sub_contractor'
  | 'designer'
  | 'food_and_travel'
  | 'fuel'
  | 'salary'
  | 'rent'
  | 'staff_advance'
  // ── SHARED / LEGACY ──
  | 'customer'
  | 'others';

/** Effective on-doc category — built-in enum OR custom-category
 *  label string. */
export type TransactionCategory = TransactionBuiltInCategory | string;

/** Status of a staff-advance parent transaction. Computed by the
 *  app layer on every child write — `open` = no bills yet,
 *  `partial` = at least one bill filed but balance not at zero,
 *  `settled` = balance reconciled (refund / reimbursement /
 *  exact match) and the user closed the advance.
 *
 *  Only meaningful when `category === 'staff_advance'`. Absent on
 *  every other transaction. */
export type AdvanceStatus = 'open' | 'partial' | 'settled';

// PaymentMethod is re-exported from `@shared/perms` at the top of
// this file.

export type TransactionStatus = 'paid' | 'pending' | 'partial';

export type TransactionWorkflowStatus = 'posted' | 'pending_approval' | 'rejected';

export type TransactionSubmissionKind = 'expense_reimbursement' | 'party_payment';

export type CreateTransactionInput = {
  projectId: string;
  orgId: string;
  type: TransactionType;
  amount: number;
  description: string;
  partyId?: string;
  partyName: string;
  category?: TransactionCategory;
  paymentMethod?: PaymentMethod;
  referenceNumber?: string;
  status: TransactionStatus;
  date: Date;
  createdBy: string;
  workflowStatus?: TransactionWorkflowStatus;
  submissionKind?: TransactionSubmissionKind;
  // Receipt photo upload is deferred; web doesn't write photoUrl(s) yet.

  // ─── Staff advance plumbing ───
  // Set on the parent advance only — caller passes 'open' when
  // creating an advance. `closeAdvance` later flips it to
  // 'partial' / 'settled' atomically via runTransaction.
  advanceStatus?: AdvanceStatus;
  advanceClosedAt?: Date;
  // Set on every CHILD (settlement / refund / reimbursement) to
  // link it back to the parent advance doc.
  parentTransactionId?: string;
  // Distinguishes a reimbursement payment_out (counted toward
  // settling the parent) from a regular settlement payment_out
  // (which decrements the parent's open balance like a normal
  // expense). Only set on reimbursement children.
  advanceRole?: 'settlement' | 'refund' | 'reimbursement';
};

export async function createTransaction(input: CreateTransactionInput): Promise<string> {
  const workflowStatus = input.workflowStatus ?? 'posted';

  const doc: Record<string, unknown> = {
    projectId: input.projectId,
    orgId: input.orgId,
    type: input.type,
    amount: input.amount,
    description: input.description,
    partyName: input.partyName,
    status: input.status,
    date: Timestamp.fromDate(input.date),
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    workflowStatus,
  };

  if (workflowStatus === 'pending_approval') {
    doc.submittedAt = serverTimestamp();
  }

  if (input.partyId) doc.partyId = input.partyId;
  if (input.category) doc.category = input.category;
  if (input.paymentMethod) doc.paymentMethod = input.paymentMethod;
  if (input.referenceNumber) doc.referenceNumber = input.referenceNumber;
  if (input.submissionKind) doc.submissionKind = input.submissionKind;

  // Staff-advance plumbing — pass-through. These fields are
  // populated by the advance-specific helpers below
  // (`createStaffAdvance`, `addAdvanceSettlement`, etc.) so the
  // generic `createTransaction` caller doesn't need to know about
  // them. Keeping them here means the helpers can compose on
  // top of `createTransaction` instead of duplicating its write.
  if (input.advanceStatus) doc.advanceStatus = input.advanceStatus;
  if (input.advanceClosedAt)
    doc.advanceClosedAt = Timestamp.fromDate(input.advanceClosedAt);
  if (input.parentTransactionId)
    doc.parentTransactionId = input.parentTransactionId;
  if (input.advanceRole) doc.advanceRole = input.advanceRole;

  const ref = await addDoc(collection(db, 'transactions'), doc);
  return ref.id;
}

/**
 * Backfill `partyId` (and optionally normalise `partyName`) on every
 * project transaction whose typed `partyName` matches the given
 * lower-cased key. Used by the Party tab's "Link to existing party" /
 * "Create new party from this name" flow to clean up rows that were
 * recorded with free-text only.
 *
 * Matches case-insensitively against the trimmed `partyName` so
 * "raju", "Raju ", and "RAJU" all collapse onto the same `partyId`.
 *
 * Returns the count of patched transactions for the caller's toast.
 *
 * Single batched write — Firestore's 500-op batch limit covers any
 * realistic per-project transaction count. For larger orgs we'd
 * chunk, but a project rarely has >500 transactions with the same
 * unlinked name.
 */
export async function linkPartyOnTransactions(args: {
  orgId: string;
  projectId: string;
  /** Lower-cased trimmed name to match against. Use the `nameKey`
   *  from `UnlinkedPartyRow`. */
  nameKey: string;
  /** Canonical name to overwrite each transaction's `partyName`
   *  with (so the saved party's casing wins). */
  partyName: string;
  /** Party doc id to set on the matching transactions. */
  partyId: string;
}): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, 'transactions'),
      where('orgId', '==', args.orgId),
      where('projectId', '==', args.projectId),
    ),
  );
  const matches = snap.docs.filter((d) => {
    const data = d.data() as { partyId?: string; partyName?: string };
    if (data.partyId) return false; // already linked
    const name = (data.partyName ?? '').trim().toLowerCase();
    return name === args.nameKey;
  });
  if (matches.length === 0) return 0;

  const batch = writeBatch(db);
  for (const d of matches) {
    batch.update(fsDoc(db, 'transactions', d.id), {
      partyId: args.partyId,
      partyName: args.partyName,
    });
  }
  await batch.commit();
  return matches.length;
}

// ───── Display label helpers (shared by table + preview) ─────

export function categoryLabel(c: TransactionCategory | undefined | null): string {
  if (!c) return '—';
  switch (c) {
    // IN
    case 'token_amount':       return 'Token Amount';
    case 'design_advance':     return 'Design Advance';
    case 'render_advance':     return '3D Render Advance';
    case 'site_mobilization':  return 'Site Mobilization';
    case 'milestone_payment':  return 'Milestone Payment';
    case 'final_payment':      return 'Final Payment';
    // OUT
    case 'labour':           return 'Labour';
    case 'material':         return 'Material';
    case 'transport':        return 'Transport';
    case 'equipment':        return 'Equipment';
    case 'sub_contractor':   return 'Sub Contractor';
    case 'designer':         return 'Designer';
    case 'food_and_travel':  return 'Food & Travel';
    case 'fuel':             return 'Fuel';
    case 'salary':           return 'Salary';
    case 'rent':             return 'Rent';
    case 'staff_advance':    return 'Staff Advance';
    // Shared / legacy
    case 'customer':         return 'Customer payment';
    case 'others':           return 'Others';
  }
  // Custom-category label persisted as the literal string on the
  // doc — show as-is. Stops the switch from being non-exhaustive
  // now that `TransactionCategory` includes `string`.
  return c;
}

export function paymentMethodLabel(m: PaymentMethod | undefined | null): string {
  if (!m) return '—';
  switch (m) {
    case 'cash':           return 'Cash';
    case 'bank_transfer':  return 'Bank Transfer';
    case 'cheque':         return 'Cheque';
    case 'upi':            return 'UPI';
  }
}

export function workflowStatusLabel(w: TransactionWorkflowStatus | undefined): string {
  switch (w) {
    case 'posted':            return 'Posted';
    case 'pending_approval':  return 'Pending';
    case 'rejected':          return 'Rejected';
    default:                  return 'Posted';
  }
}

/** Flat option list (every key). Kept for the ledger filter UI
 *  where the user can pick across types. The Add-Transaction picker
 *  uses the type-scoped lists below instead. */
export const TRANSACTION_CATEGORY_OPTIONS: { value: TransactionBuiltInCategory; label: string }[] = [
  { value: 'token_amount',      label: 'Token Amount' },
  { value: 'design_advance',    label: 'Design Advance' },
  { value: 'render_advance',    label: '3D Render Advance' },
  { value: 'site_mobilization', label: 'Site Mobilization' },
  { value: 'milestone_payment', label: 'Milestone Payment' },
  { value: 'final_payment',     label: 'Final Payment' },
  { value: 'labour',            label: 'Labour' },
  { value: 'material',          label: 'Material' },
  { value: 'transport',         label: 'Transport' },
  { value: 'equipment',         label: 'Equipment' },
  { value: 'sub_contractor',    label: 'Sub Contractor' },
  { value: 'designer',          label: 'Designer' },
  { value: 'food_and_travel',   label: 'Food & Travel' },
  { value: 'fuel',              label: 'Fuel' },
  { value: 'salary',            label: 'Salary' },
  { value: 'rent',              label: 'Rent' },
  { value: 'staff_advance',     label: 'Staff Advance' },
  { value: 'customer',          label: 'Customer payment' },
  { value: 'others',            label: 'Others' },
];

/** Type-scoped category options for the Add-Transaction picker.
 *  Mirrors `TRANSACTION_CATEGORIES_BY_TYPE` on mobile. */
export const TRANSACTION_CATEGORY_OPTIONS_BY_TYPE: Record<
  TransactionType,
  { value: TransactionCategory; label: string }[]
> = {
  payment_in: [
    { value: 'token_amount',      label: 'Token Amount' },
    { value: 'design_advance',    label: 'Design Advance' },
    { value: 'render_advance',    label: '3D Render Advance' },
    { value: 'site_mobilization', label: 'Site Mobilization' },
    { value: 'milestone_payment', label: 'Milestone Payment' },
    { value: 'final_payment',     label: 'Final Payment' },
    { value: 'others',            label: 'Others' },
  ],
  payment_out: [
    { value: 'labour',         label: 'Labour' },
    { value: 'material',       label: 'Material' },
    { value: 'transport',      label: 'Transport' },
    { value: 'equipment',      label: 'Equipment' },
    { value: 'sub_contractor', label: 'Sub Contractor' },
    { value: 'designer',       label: 'Designer' },
    { value: 'food_and_travel',label: 'Food & Travel' },
    { value: 'fuel',           label: 'Fuel' },
    { value: 'salary',         label: 'Salary' },
    { value: 'rent',           label: 'Rent' },
    // Staff advance — a cash float given to a site staff member who
    // settles bills against it via the advance detail screen. Mirror
    // of the mobile picker in `src/features/transactions/types.ts`.
    // Picking this routes the create through `createStaffAdvance`.
    { value: 'staff_advance',  label: 'Staff Advance' },
    { value: 'others',         label: 'Others' },
  ],
};

export const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'upi',           label: 'UPI' },
];

// ─────────────────────────────────────────────────────────────
// STAFF ADVANCE HELPERS
// ─────────────────────────────────────────────────────────────
//
// Domain model recap (see plan + docs):
//
//   - Parent advance      → payment_out, category='staff_advance',
//                           advanceStatus='open'.
//   - Settlement (child)  → payment_out, parentTransactionId set,
//                           advanceRole='settlement', real
//                           category (NOT 'staff_advance').
//   - Refund (child)      → payment_in, parentTransactionId set,
//                           advanceRole='refund'.
//   - Reimbursement       → payment_out, parentTransactionId set,
//                           advanceRole='reimbursement'.
//                           Studio pays the staff member back for
//                           overspending out of their own pocket.
//
// All writes that touch the parent's status go through
// runTransaction() so concurrent bill writes can't leave the
// parent's status field stale.

/** Shape used by `computeAdvanceBalance`. Compatible with both the
 *  web `TransactionItem` and any minimal Firestore-doc shape. We
 *  define it loosely so the same helper works from both the live
 *  hook layer AND inside a Firestore transaction (where docs are
 *  raw DocumentSnapshot data). */
export type AdvanceChild = {
  type?: string;
  amount: number;
  advanceRole?: 'settlement' | 'refund' | 'reimbursement';
  workflowStatus?: 'posted' | 'pending_approval' | 'rejected';
};

export type AdvanceParent = {
  amount: number;
  advanceStatus?: AdvanceStatus;
};

export type AdvanceBalance = {
  /** Sum of `payment_out` children with role='settlement'.
   *  Excludes rejected children. */
  spent: number;
  /** Sum of `payment_in` children with role='refund'. */
  refunded: number;
  /** Sum of `payment_out` children with role='reimbursement'.
   *  These INCREASE the wallet (studio paying back the staff). */
  reimbursed: number;
  /** Live wallet balance: positive = engineer still holds cash,
   *  zero = exactly reconciled, negative = engineer overspent
   *  and needs to be reimbursed by the studio. */
  balance: number;
  /** Derived from balance + child count, NOT directly settable. */
  status: AdvanceStatus;
};

/** Pure: given a parent + its children, returns the live numbers.
 *  No I/O. Same impl used on web + mobile so behaviour is
 *  identical regardless of which client triggers the recompute. */
export function computeAdvanceBalance(
  parent: AdvanceParent,
  children: AdvanceChild[],
): AdvanceBalance {
  let spent = 0;
  let refunded = 0;
  let reimbursed = 0;
  let postedCount = 0;
  for (const c of children) {
    // Rejected children don't affect the wallet — they were
    // submitted and bounced. Pending children DO count (the
    // engineer is asking us to acknowledge those bills; we treat
    // them as committed for balance-tracking purposes, even
    // though the workflow status is still 'pending_approval').
    if (c.workflowStatus === 'rejected') continue;
    postedCount += 1;
    const role = c.advanceRole;
    const isIn = c.type === 'payment_in' || c.type === 'income';
    if (role === 'reimbursement') reimbursed += c.amount;
    else if (role === 'refund' || (isIn && !role)) refunded += c.amount;
    else spent += c.amount;
  }
  const balance = parent.amount + reimbursed - spent - refunded;

  // Status flips only when the user explicitly closes the
  // advance — `computeAdvanceBalance` is the source of truth
  // for the "is this settleable yet?" question but the caller
  // decides when to write 'settled'. While the user is still
  // filing bills, status sits at 'open' (no bills) or 'partial'
  // (some bills filed, balance pending close).
  const status: AdvanceStatus =
    parent.advanceStatus === 'settled'
      ? 'settled'
      : postedCount === 0
        ? 'open'
        : 'partial';

  return { spent, refunded, reimbursed, balance, status };
}

/** Re-reads parent + all children inside a Firestore runTransaction
 *  and writes the recomputed status back to the parent doc.
 *  Called automatically by every helper that mutates an advance.
 *  Safe to call standalone too — idempotent. */
async function recomputeAdvanceStatusInline(
  parentId: string,
  // Allow callers inside an outer runTransaction to pass it in so
  // the recompute happens atomically with their write. If null
  // we open our own runTransaction.
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const parentRef = fsDoc(db, 'transactions', parentId);
    const parentSnap = await tx.get(parentRef);
    if (!parentSnap.exists()) return; // already deleted; nothing to do
    const parentData = parentSnap.data() as Record<string, unknown>;
    const parent: AdvanceParent = {
      amount: (parentData.amount as number) ?? 0,
      advanceStatus: parentData.advanceStatus as AdvanceStatus | undefined,
    };

    // Children are fetched via the (orgId, parentTransactionId)
    // index. We have to do this outside the transaction since
    // Firestore transactions don't support queries — only direct
    // gets. We accept the small race window: a child written
    // BETWEEN our query and our parent write would miss the
    // recompute, but the NEXT write (or close-advance) will
    // catch it up.
    // For safety, the per-write helpers (addAdvanceSettlement etc)
    // do their own transactional read of the child + parent;
    // this fn is the "background reconcile" path that runs after
    // every change.
    const orgId = (parentData.orgId as string) ?? '';
    const childrenSnap = await getDocs(
      query(
        collection(db, 'transactions'),
        where('orgId', '==', orgId),
        where('parentTransactionId', '==', parentId),
      ),
    );
    const children: AdvanceChild[] = childrenSnap.docs.map((d) => {
      const x = d.data() as Record<string, unknown>;
      return {
        type: x.type as string | undefined,
        amount: (x.amount as number) ?? 0,
        advanceRole: x.advanceRole as AdvanceChild['advanceRole'],
        workflowStatus: x.workflowStatus as AdvanceChild['workflowStatus'],
      };
    });

    const computed = computeAdvanceBalance(parent, children);
    if (computed.status !== parent.advanceStatus) {
      tx.update(parentRef, { advanceStatus: computed.status });
    }
  });
}

/** Create a parent staff advance — payment_out, category=
 *  'staff_advance', advanceStatus='open'. The cash leaves the
 *  studio's account once on creation; subsequent bills (children)
 *  do NOT move cash, they label where the advance went. */
export async function createStaffAdvance(input: {
  orgId: string;
  projectId: string;
  staffPartyId: string;
  staffPartyName: string;
  amount: number;
  description?: string;
  paymentMethod?: PaymentMethod;
  date?: Date;
  createdBy: string;
  workflowStatus?: TransactionWorkflowStatus;
}): Promise<string> {
  if (input.amount <= 0) throw new Error('Advance amount must be > 0');
  if (!input.staffPartyId)
    throw new Error('Staff party is required for an advance');

  return createTransaction({
    orgId: input.orgId,
    projectId: input.projectId,
    type: 'payment_out',
    amount: input.amount,
    description: input.description ?? 'Staff advance for site expenses',
    partyId: input.staffPartyId,
    partyName: input.staffPartyName,
    category: 'staff_advance',
    paymentMethod: input.paymentMethod ?? 'cash',
    status: 'paid',
    date: input.date ?? new Date(),
    createdBy: input.createdBy,
    workflowStatus: input.workflowStatus ?? 'posted',
    advanceStatus: 'open',
  });
}

/** Add a bill paid from the advance. CHILD payment_out — NOT a
 *  fresh outflow of cash; just labels how the advance was spent.
 *  Wrapped in runTransaction so concurrent bill writes serialise
 *  on the parent's status field. */
export async function addAdvanceSettlement(input: {
  parentTransactionId: string;
  orgId: string;
  projectId: string;
  vendorPartyId?: string;
  vendorPartyName: string;
  amount: number;
  category: Exclude<TransactionCategory, 'staff_advance'>;
  description?: string;
  paymentMethod?: PaymentMethod;
  referenceNumber?: string;
  date?: Date;
  createdBy: string;
  workflowStatus?: TransactionWorkflowStatus;
}): Promise<string> {
  if (input.amount <= 0) throw new Error('Bill amount must be > 0');
  // Runtime guard against JS callers that bypass the
  // `Exclude<…, 'staff_advance'>` type — cast to string so TS
  // doesn't optimise the check away as provably-false.
  if ((input.category as string) === 'staff_advance')
    throw new Error('Bills against an advance cannot themselves be advances');

  // Transactional pre-check: parent must exist + be a real
  // advance + not already settled. We do this in a runTransaction
  // so two simultaneous writes both see the latest parent state.
  await runTransaction(db, async (tx) => {
    const parentRef = fsDoc(db, 'transactions', input.parentTransactionId);
    const parentSnap = await tx.get(parentRef);
    if (!parentSnap.exists())
      throw new Error('Advance not found — it may have been deleted');
    const parent = parentSnap.data() as Record<string, unknown>;
    if (parent.category !== 'staff_advance')
      throw new Error('Target transaction is not a staff advance');
    if (parent.advanceStatus === 'settled')
      throw new Error('Cannot file a bill against a settled advance');
    // The status flip from 'open' → 'partial' happens here so the
    // parent UI updates as soon as the first bill lands. Recompute
    // pass after the write will handle 'partial' → 'partial' (no
    // change) and any future 'settled' flips via closeAdvance.
    if (parent.advanceStatus !== 'partial') {
      tx.update(parentRef, { advanceStatus: 'partial' });
    }
  });

  // Write the child outside the transaction (addDoc isn't part of
  // Firestore transactions in the web SDK for new ids). Then run
  // a recompute to settle any race where a sibling write also
  // landed concurrently.
  const childId = await createTransaction({
    orgId: input.orgId,
    projectId: input.projectId,
    type: 'payment_out',
    amount: input.amount,
    description: input.description ?? `Bill — ${input.vendorPartyName}`,
    partyId: input.vendorPartyId,
    partyName: input.vendorPartyName,
    category: input.category,
    paymentMethod: input.paymentMethod ?? 'cash',
    referenceNumber: input.referenceNumber,
    status: 'paid',
    date: input.date ?? new Date(),
    createdBy: input.createdBy,
    workflowStatus: input.workflowStatus ?? 'posted',
    parentTransactionId: input.parentTransactionId,
    advanceRole: 'settlement',
  });
  await recomputeAdvanceStatusInline(input.parentTransactionId);
  return childId;
}

/** Engineer returned leftover cash. CHILD payment_in linked to
 *  the parent advance. Bumps the studio's cash position back up. */
export async function recordAdvanceRefund(input: {
  parentTransactionId: string;
  orgId: string;
  projectId: string;
  staffPartyId: string;
  staffPartyName: string;
  amount: number;
  paymentMethod?: PaymentMethod;
  date?: Date;
  createdBy: string;
}): Promise<string> {
  if (input.amount <= 0) throw new Error('Refund amount must be > 0');
  const childId = await createTransaction({
    orgId: input.orgId,
    projectId: input.projectId,
    type: 'payment_in',
    amount: input.amount,
    description: 'Advance refund — leftover cash returned',
    partyId: input.staffPartyId,
    partyName: input.staffPartyName,
    paymentMethod: input.paymentMethod ?? 'cash',
    status: 'paid',
    date: input.date ?? new Date(),
    createdBy: input.createdBy,
    workflowStatus: 'posted',
    parentTransactionId: input.parentTransactionId,
    advanceRole: 'refund',
  });
  await recomputeAdvanceStatusInline(input.parentTransactionId);
  return childId;
}

/** Engineer overspent — studio writes a fresh payment_out to him
 *  for the gap. Linked to the parent so the audit trail is
 *  complete + the overspend doesn't pollute the project's
 *  cash-out math for category reports (its category is 'others'
 *  by convention). */
export async function recordAdvanceReimbursement(input: {
  parentTransactionId: string;
  orgId: string;
  projectId: string;
  staffPartyId: string;
  staffPartyName: string;
  amount: number;
  paymentMethod?: PaymentMethod;
  date?: Date;
  createdBy: string;
}): Promise<string> {
  if (input.amount <= 0) throw new Error('Reimbursement amount must be > 0');
  const childId = await createTransaction({
    orgId: input.orgId,
    projectId: input.projectId,
    type: 'payment_out',
    amount: input.amount,
    description: 'Reimbursement — staff overspend on advance',
    partyId: input.staffPartyId,
    partyName: input.staffPartyName,
    category: 'others',
    paymentMethod: input.paymentMethod ?? 'cash',
    status: 'paid',
    date: input.date ?? new Date(),
    createdBy: input.createdBy,
    workflowStatus: 'posted',
    parentTransactionId: input.parentTransactionId,
    advanceRole: 'reimbursement',
  });
  await recomputeAdvanceStatusInline(input.parentTransactionId);
  return childId;
}

/** Mark a parent advance as 'settled'. Refuses unless the live
 *  balance is exactly zero, OR the caller passes `force: true`
 *  for the write-off path (e.g. user accepts a ₹50 rounding gap).
 *  Atomically updates `advanceStatus` + `advanceClosedAt`. */
export async function closeAdvance(input: {
  parentTransactionId: string;
  force?: boolean;
}): Promise<void> {
  await runTransaction(db, async (tx) => {
    const parentRef = fsDoc(db, 'transactions', input.parentTransactionId);
    const parentSnap = await tx.get(parentRef);
    if (!parentSnap.exists()) throw new Error('Advance not found');
    const parentData = parentSnap.data() as Record<string, unknown>;
    if (parentData.category !== 'staff_advance')
      throw new Error('Target transaction is not a staff advance');

    // Read children outside the tx (Firestore tx can't query).
    // For close-advance the race window is acceptable — closing
    // is a deliberate user gesture, not a write-heavy auto-path.
    const orgId = (parentData.orgId as string) ?? '';
    const childrenSnap = await getDocs(
      query(
        collection(db, 'transactions'),
        where('orgId', '==', orgId),
        where('parentTransactionId', '==', input.parentTransactionId),
      ),
    );
    const parent: AdvanceParent = {
      amount: (parentData.amount as number) ?? 0,
      advanceStatus: parentData.advanceStatus as AdvanceStatus | undefined,
    };
    const children: AdvanceChild[] = childrenSnap.docs.map((d) => {
      const x = d.data() as Record<string, unknown>;
      return {
        type: x.type as string | undefined,
        amount: (x.amount as number) ?? 0,
        advanceRole: x.advanceRole as AdvanceChild['advanceRole'],
        workflowStatus: x.workflowStatus as AdvanceChild['workflowStatus'],
      };
    });
    const live = computeAdvanceBalance(parent, children);
    if (live.balance !== 0 && !input.force) {
      throw new Error(
        `Cannot close — balance is ₹${live.balance.toLocaleString('en-IN')}. ` +
          'File a refund / reimbursement first, or pass force=true to write off.',
      );
    }

    tx.update(parentRef, {
      advanceStatus: 'settled',
      advanceClosedAt: serverTimestamp(),
    });
  });
}

/** Unapply a settlement child from its parent advance — the
 *  bill stays in Firestore as a regular `payment_out` row with
 *  no `parentTransactionId` and no `advanceRole`. After the
 *  unlink, the parent's status is recomputed from its remaining
 *  children (may flip PARTIAL → OPEN if this was the last bill).
 *
 *  Use case: admin accidentally linked a bill to the wrong
 *  advance via the smart-link prompt at submission time, or
 *  wants to convert a child into a vendor-direct payment. Rare
 *  but real — escape hatch refinement #5 from the Concur
 *  industry research.
 *
 *  Permission gate is at the UI level — finance roles only. */
export async function unapplyAdvanceSettlement(
  childTransactionId: string,
): Promise<void> {
  // First fetch the parent id so we know what to recompute.
  // Done outside the runTransaction because Firestore txns
  // can't run their own follow-up queries cleanly.
  const childRef = fsDoc(db, 'transactions', childTransactionId);
  let parentId: string | null = null;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(childRef);
    if (!snap.exists()) throw new Error('Bill not found');
    const data = snap.data() as Record<string, unknown>;
    const pid = data.parentTransactionId as string | undefined;
    if (!pid) {
      // Already unlinked — nothing to do, treat as success.
      return;
    }
    parentId = pid;
    tx.update(childRef, {
      parentTransactionId: deleteField(),
      advanceRole: deleteField(),
    });
  });
  if (parentId) {
    await recomputeAdvanceStatusInline(parentId);
  }
}

/** Friendly label for the AdvanceStatus pill. */
export function advanceStatusLabel(s: AdvanceStatus | undefined): string {
  switch (s) {
    case 'open':    return 'OPEN';
    case 'partial': return 'PARTIAL';
    case 'settled': return 'SETTLED';
    default:        return 'OPEN';
  }
}

/** Visual tone for the pill (bg/fg pair matching the iio.css
 *  semantic colour ramp used elsewhere on the project page). */
export function advanceStatusTone(
  s: AdvanceStatus | undefined,
): { bg: string; fg: string } {
  switch (s) {
    case 'partial': return { bg: '#DBEAFE', fg: '#1D4ED8' };
    case 'settled': return { bg: '#DCFCE7', fg: '#15803D' };
    case 'open':
    default:        return { bg: '#FEF3C7', fg: '#B45309' };
  }
}
